import React, { createContext, useContext, useEffect, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { signInWithPopup, GoogleAuthProvider, OAuthProvider, linkWithCredential, User as FirebaseUser, signInWithEmailAndPassword, setPersistence, browserLocalPersistence, signInWithCustomToken } from 'firebase/auth';

import { auth, scopeAuthToTab } from '@/lib/firebase';
import { signInAnonymously } from 'firebase/auth';
import { SIGN_IN,LINK_ACCOUNT, SIGN_IN_WEB, SIGN_IN_WITH_INTERVIEW_LINK, SIGN_IN_WITH_INVITE_LINK } from '@/api/graphql/mutations';
import { CURRENT_USER } from '@/api/graphql/queries';
import { ErrorLog } from '@/lib/sentry';
import { User, SignInResponse, LinkAccountResponse, ExtraAuthData } from '@/types/api';
import { storeAuthToken, removeAuthToken, extractInviteIdFromPath, extractCheckInIdFromPath } from './utils';

/** What a texted group interview invite's key got: a session, a sign-up first, or nothing. */
export type InviteLinkSignInResult =
  | { status: 'signed-in' }
  | { status: 'needs-registration'; phone: string | null }
  | { status: 'refused' };

// sessionStorage key for "which invitation token did we last sign in for".
// Firebase persists anonymous users across page loads via its own storage,
// so when a user opens a NEW invitation URL in the same tab the prior
// invitation's Firebase user gets resurrected and (without intervention)
// the page renders the prior contributor's name and the next request sends
// the prior auth token. This key lets us detect the mismatch in
// onIdTokenChanged and force the resurrected user to sign out so
// signInGuestContributor for the NEW invite can take over.
//
// It lives in sessionStorage — NOT localStorage — to match the tab-scoped
// persistence of guest sessions (see firebase.ts): each tab tracks its own
// guest user, so a shared browser-wide key would let two tabs holding
// different invitations trip each other's stale-session guard.
export const SIGNED_IN_FOR_INVITE_KEY = 'proxiSignedInForInvite';

const provider = new GoogleAuthProvider();
const linkedInProvider = new OAuthProvider('oidc.linkedin');

interface PhoneSignInResult {
  user?: User;
  needsLinking?: boolean;
  conflictingMethod?: 'email' | 'phone';
  maskedContact?: string;
  error?: string;
}

interface AuthContextType {
  authUser: FirebaseUser | null;
  currentUser: User | null;
  loading: boolean;
  isGuestSigningIn: boolean;
  signIn: () => void;
  signInWithGoogle: () => Promise<User | undefined>;
  signInWithLinkedIn: () => Promise<User | undefined>;
  signInWithEmailPassword: (email: string, password: string) => Promise<User | undefined>;
  signInWithPhone: (
    phoneIdToken: string,
    phoneE164: string,
    extra?: { invitationToken?: string; inviteLinkKey?: string },
  ) => Promise<PhoneSignInResult>;
  signInGuestContributor: (invitationData: any, token: string) => Promise<void>;
  signInWithInterviewLink: (interviewId: string, key: string) => Promise<boolean>;
  signInWithInviteLink: (token: string, key: string) => Promise<InviteLinkSignInResult>;
  /**
   * The check-in id or invite token when this tab's session came from a texted
   * Archive interview link — a session that only answers interviews. Null
   * otherwise.
   */
  linkInterviewId: string | null;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAuthProgress, setIsAuthProgress] = useState(false);
  const [isGuestSigningIn, setIsGuestSigningIn] = useState(false);
  const [authUser, setAuthUser] = useState<FirebaseUser | null>(null);
  const [linkInterviewId, setLinkInterviewId] = useState<string | null>(null);
  const [tokenRefreshAttempted, setTokenRefreshAttempted] = useState(false);

  const apolloClient = useApolloClient();
  // Skip until Firebase has restored a user and we've stored a fresh token,
  // otherwise this useQuery fires synchronously on mount with a stale/missing
  // token and the resolver rejects it. Apollo doesn't auto-retry, so `me`
  // would stay undefined for the rest of the session — leaving callers that
  // gate on `currentUser._id` (e.g. WakeOverlay's `userBoundToInvite`) stuck.
  const { data, loading, error, refetch } = useQuery(CURRENT_USER, {
    skip: isInitializing,
  });
  const [signInMutation] = useMutation<SignInResponse>(SIGN_IN);
  const [linkAccount] = useMutation<LinkAccountResponse>(LINK_ACCOUNT);
  const [signInWebMutation] = useMutation(SIGN_IN_WEB);
  const [signInWithInterviewLinkMutation] = useMutation(SIGN_IN_WITH_INTERVIEW_LINK);
  const [signInWithInviteLinkMutation] = useMutation(SIGN_IN_WITH_INVITE_LINK);

/* useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user: FirebaseUser | null) => {
      if (user) {
        const token = await user.getIdToken(true);
        storeAuthToken(token);
        try {
          await signInMutation({ variables: { token }, refetchQueries: [CURRENT_USER], awaitRefetchQueries: true });
          setIsAuthProgress(false);
        } catch (e) {
          // Authentication error - will be handled by auth state
        }
      }
      // Removed automatic anonymous signin - will be handled explicitly for guest contributors
      setAuthUser(user);
      setIsInitializing(false);
    });

    return unsubscribe;
  }, []);
  */

  useEffect(() => {
    const unsubscribe = auth.onIdTokenChanged(async (user: FirebaseUser | null) => {
      if (user) {
        // If this Firebase user is anonymous and was signed in for a
        // DIFFERENT invitation than the one currently in the URL, force
        // them out instead of storing their token. Firebase persists anon
        // users across page loads, so without this the prior invitation's
        // user comes back to life on the new URL — the wake screen ends
        // up showing the previous contributor's name and Start fires
        // requests with the wrong auth (or, after the apollo-provider's
        // async signOut races in, no auth at all → "You have to provide
        // user").
        if (user.isAnonymous) {
          const currentInvite = extractInviteIdFromPath();
          const signedInFor = sessionStorage.getItem(SIGNED_IN_FOR_INVITE_KEY);
          // Clean up the browser-wide copy older deploys wrote to
          // localStorage so it can't resurface if this key ever moves again.
          localStorage.removeItem(SIGNED_IN_FOR_INVITE_KEY);
          if (currentInvite && signedInFor && signedInFor !== currentInvite) {
            // Stale anon user from a previous invitation. Sign them out
            // and let signInGuestContributor (fired from the
            // GUEST_CONTRIBUTOR / INTERVIEW effects when currentUser
            // becomes null) re-bind a fresh Firebase user to the current
            // invitation's predefined participant on the backend.
            await auth.signOut();
            return;
          }
        }
        // A session opened by a texted Archive interview link only answers
        // interviews (the API refuses it anything else), so it is only good
        // on a check-in or invite page: anywhere else, sign it out so the
        // person signs in properly.
        let linkClaim: string | null = null;
        if (!user.isAnonymous) {
          const claim = await user
            .getIdTokenResult()
            .then(result => result.claims.interviewLink)
            .catch(() => undefined);
          linkClaim = typeof claim === 'string' && claim ? claim : null;
        }
        if (linkClaim && !extractCheckInIdFromPath() && !extractInviteIdFromPath()) {
          await auth.signOut();
          return;
        }
        setLinkInterviewId(linkClaim);
        // Real accounts — any user with a linked provider (Google, LinkedIn,
        // email/password, phone) — get browser-wide persistence so returning
        // visitors stay signed in across restarts. Guests (anonymous users
        // and per-invitation custom-token users, which have no providerData)
        // keep the tab-scoped persistence chosen at their sign-in site — see
        // firebase.ts. Doing this centrally on the token stream covers every
        // real sign-in path, including anonymous users upgraded via
        // linkWithCredential, without touching each call site. A link
        // session is the recipient's own login but stays tab-scoped too.
        if (!user.isAnonymous && user.providerData.length > 0 && !linkClaim) {
          await setPersistence(auth, browserLocalPersistence).catch(() => {
            // Storage unavailable — the session stays wherever it was
            // signed in (worst case: it lasts only this tab, as before).
          });
        }
        try {
          // Force refresh token to ensure it's valid
          const token = await user.getIdToken(true);
          storeAuthToken(token);
          // Re-pull `me` now that we have a fresh token. The initial
          // useQuery was skipped while isInitializing was true; this call
          // (and subsequent token swaps) is what kicks off the actual fetch.
          refetch().catch(() => {});
        } catch (error) {
          // Token refresh failed - clear auth state
          removeAuthToken();
          await auth.signOut();
        }
      } else {
        // No user - ensure token is removed
        removeAuthToken();
        setLinkInterviewId(null);
        // Write `me: null` directly into the Apollo cache so subscribers of
        // useQuery(CURRENT_USER) immediately see `currentUser = null` and
        // the GUEST_CONTRIBUTOR / INTERVIEW effects can fall through their
        // mismatch checks to signInGuestContributor for the NEW invite.
        // useQuery doesn't auto-refetch on Firebase auth changes, and a
        // bare `cache.evict` doesn't reliably trigger cache-first watchers
        // to re-pull either — without this explicit write, `currentUser`
        // stays stuck on the previous invitation's user. The wake screen
        // renders the prior contributor's name, and clicking Start fires
        // getNextMessagesInterview with no token in localStorage (cleared
        // above) → backend rejects with "You have to provide user".
        try {
          apolloClient.writeQuery({
            query: CURRENT_USER,
            data: { me: null },
          });
        } catch {
          // writeQuery can throw if the query isn't registered yet on the
          // very first mount; safe to ignore — there's no stale `me` to
          // overwrite in that case.
        }
      }
      setAuthUser(user);
      setIsInitializing(false);
    });
    return unsubscribe;
  }, [apolloClient, refetch]);

  // Handle token expiration on app initialization/return
  useEffect(() => {
    const handleTokenExpiration = async () => {
      // Skip if already attempted or still initializing
      if (tokenRefreshAttempted || isInitializing) return;

      const currentUser = auth.currentUser;
      
      // If we have a user, proactively check if token is valid
      if (currentUser) {
        try {
          // This will throw if the token is expired and can't be refreshed
          await currentUser.getIdToken(false);
        } catch (error: any) {
          
          try {
            // Try to force refresh the token
            const newToken = await currentUser.getIdToken(true);
            storeAuthToken(newToken);
            
            // Optionally re-authenticate with backend
            if (signInMutation) {
              await signInMutation({
                variables: { token: newToken },
                refetchQueries: [CURRENT_USER],
                awaitRefetchQueries: true
              });
            }
          } catch (refreshError) {
            // Session cannot be recovered - clean up
            removeAuthToken();
            await auth.signOut();
            // User will need to sign in again
          }
        }
      }
      
      setTokenRefreshAttempted(true);
    };

    // Run token expiration check after initialization
    if (!isInitializing) {
      handleTokenExpiration();
    }
  }, [isInitializing, tokenRefreshAttempted, signInMutation]);

  const signIn = () => {
    setIsAuthProgress(true);
    scopeAuthToTab()
      .then(() => signInAnonymously(auth))
      .then(() => {
        // Anonymous sign-in successful
      })
      .catch((error) => {
        // Anonymous sign-in failed
      });
  };

  const signInGuestContributor = async (invitationData: any, token: string) => {
    if (invitationData?.invitation?.type === 'GUEST_CONTRIBUTOR' || invitationData?.invitation?.type === 'INTERVIEW') {
      try {
        setIsGuestSigningIn(true);

        // Bind this invite token to the SIGNED_IN_FOR_INVITE_KEY BEFORE
        // signInAnonymously fires. onIdTokenChanged runs synchronously off
        // the new Firebase user and compares signedInFor vs currentInvite —
        // if the previous invite's value is still in sessionStorage, our
        // just-created anon user is flagged as stale and force-signed-out,
        // leaving me to refetch with no/wrong token and the prior
        // invitation's user leaks into currentUser.
        sessionStorage.setItem(SIGNED_IN_FOR_INVITE_KEY, token);

        // Keep this guest session scoped to the tab so other invitation
        // links (in other tabs, or opened later) each get their own user.
        await scopeAuthToTab();

        // Sign in anonymously with Firebase
        const userCredential = await signInAnonymously(auth);
        const authToken = await userCredential.user.getIdToken();
        
        // Split fullName into firstName and lastName
        const fullName = invitationData.invitation?.fullName || '';
        const nameParts = fullName.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        // Determine user type based on invitation type
        const userType = invitationData.invitation?.type === 'INTERVIEW' ? 'MEMBER' : 'GUEST_CONTRIBUTOR';
        
        // Prepare extraData following ExtraAuthData interface
        const extraData: ExtraAuthData = {
          email: invitationData.invitation?.email,
          firstName,
          displayName: fullName,
          lastName,
          userType,
          invitationToken: token
        };
        
        // Sign in with web mutation
        await signInWebMutation({
          variables: { token: authToken, extraData },
          refetchQueries: [CURRENT_USER],
          awaitRefetchQueries: true
        });

        setIsGuestSigningIn(false);
      } catch (error) {
        const userTypeLabel = invitationData.invitation?.type === 'INTERVIEW' ? 'Interview participant' : 'Guest contributor';
        setIsGuestSigningIn(false);
        throw error;
      }
    }
  };

  // A texted Archive interview link (/interview/<id>?k=… or /invite/<token>?k=…)
  // signs its recipient in without a sign-in screen: the API checks the key and
  // hands back a custom token for a session that only answers interviews.
  // Tab-scoped like a guest's, and resolves once `me` is loaded.
  const signInWithLinkToken = async (customToken: string) => {
    await scopeAuthToTab();
    const credential = await signInWithCustomToken(auth, customToken);
    storeAuthToken(await credential.user.getIdToken());
    await refetch();
  };

  // False when the key is refused (forged, or the text is too old) — the page
  // then asks them to sign in.
  const signInWithInterviewLink = async (interviewId: string, key: string) => {
    try {
      const { data } = await signInWithInterviewLinkMutation({ variables: { interviewId, key } });
      const result = data?.signInWithInterviewLink;
      if (!result?.success || !result.customToken) return false;
      await signInWithLinkToken(result.customToken);
      return true;
    } catch (error) {
      ErrorLog(error);
      return false;
    }
  };

  // A group interview invite. An invitee with no login yet signs up once
  // (with the number it was texted to) instead; every link after that skips it.
  const signInWithInviteLink = async (token: string, key: string): Promise<InviteLinkSignInResult> => {
    try {
      const { data } = await signInWithInviteLinkMutation({ variables: { token, key } });
      const result = data?.signInWithInviteLink;
      if (result?.needsRegistration) return { status: 'needs-registration', phone: result.phone ?? null };
      if (!result?.success || !result.customToken) return { status: 'refused' };
      await signInWithLinkToken(result.customToken);
      return { status: 'signed-in' };
    } catch (error) {
      ErrorLog(error);
      return { status: 'refused' };
    }
  };

  const signInWithGoogle = async () => {
    try {
      // First, sign out any existing user to ensure clean state
      if (auth.currentUser) {
        await auth.signOut();
        removeAuthToken();
      }
      
      // Sign in with Google popup
      const result = await signInWithPopup(auth, provider);
      
      // Get fresh Firebase ID token
      const firebaseToken = await result.user.getIdToken(true);
      
      // Store the Firebase token
      storeAuthToken(firebaseToken);
      
      // Get Google credential for linking
      const credential = GoogleAuthProvider.credentialFromResult(result);
      
      // Only try to link if there's a current user and credential
      // Skip linking if provider is already linked (happens on re-authentication)
      if (auth.currentUser && credential) {
        try {
          await linkWithCredential(auth.currentUser, credential);
        } catch (err: any) {
          // Link credential error - not critical
          // This can happen if the provider is already linked
        }
      }
      
      const googleAccessToken = credential?.accessToken;
      
      // For existing users signing in, use SIGN_IN mutation instead of LINK_ACCOUNT
      // LINK_ACCOUNT is primarily for linking additional providers to an existing account
      const { data } = await signInMutation({
        variables: { token: firebaseToken },
        refetchQueries: [CURRENT_USER],
        awaitRefetchQueries: true
      });
      
      return data?.signIn?.user;
    } catch (error: any) {
      ErrorLog(error);
      throw error;
    }
  };

  const signInWithLinkedIn = async () => {
    try {
      // First, sign out any existing user to ensure clean state
      if (auth.currentUser) {
        await auth.signOut();
        removeAuthToken();
      }
      
      // Sign in with LinkedIn popup
      const result = await signInWithPopup(auth, linkedInProvider);
      
      // Get fresh Firebase ID token
      const firebaseToken = await result.user.getIdToken(true);
      
      // Store the Firebase token
      storeAuthToken(firebaseToken);
      
      // Get LinkedIn credential for linking
      const credential = OAuthProvider.credentialFromResult(result);
      
      // Only try to link if there's a current user and credential
      if (auth.currentUser && credential) {
        try {
          await linkWithCredential(auth.currentUser, credential);
        } catch (err: any) {
          // Link credential error - not critical
        }
      }
      
      // Sign in with backend using Firebase token
      const { data } = await signInMutation({
        variables: { token: firebaseToken },
        refetchQueries: [CURRENT_USER],
        awaitRefetchQueries: true
      });
      
      return data?.signIn?.user;
    } catch (error: any) {
      ErrorLog(error);
      throw error;
    }
  };

  const signInWithPhone = async (
    phoneIdToken: string,
    phoneE164: string,
    extra: { invitationToken?: string; inviteLinkKey?: string } = {},
  ): Promise<PhoneSignInResult> => {
    try {
      // The phone-auth Firebase user is already signed in by signInWithPhoneNumber.
      // Just persist the token and call signInWeb with phone-shaped extraData.
      storeAuthToken(phoneIdToken);

      const { data } = await signInWebMutation({
        variables: {
          token: phoneIdToken,
          extraData: {
            phoneNumber: phoneE164,
            userType: 'MEMBER',
            invitationToken: extra.invitationToken,
            // A texted invite's key shows they got the text, so the API lets
            // this sign-up become the invitee's login.
            inviteLinkKey: extra.inviteLinkKey,
          },
        },
        refetchQueries: [CURRENT_USER],
        awaitRefetchQueries: true,
      });

      const result = data?.signInWeb;
      if (result?.needsLinking) {
        // Sign the user out of Firebase so they can re-auth via their original
        // method without the phone-auth user lingering.
        try {
          await auth.signOut();
          removeAuthToken();
        } catch {
          // ignore
        }
        return {
          needsLinking: true,
          conflictingMethod: result.conflictingMethod,
          maskedContact: result.maskedContact,
        };
      }

      if (!result?.success) {
        return { error: result?.error || 'Sign-in failed.' };
      }

      return { user: result.user };
    } catch (error: any) {
      ErrorLog(error);
      return { error: error?.message || 'Sign-in failed.' };
    }
  };

  const signInWithEmailPassword = async (email: string, password: string) => {
    try {
      // First, sign out any existing user to ensure clean state
      if (auth.currentUser) {
        await auth.signOut();
        removeAuthToken();
      }
      
      // Sign in with email/password
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Force refresh to get a fresh, valid token
      const token = await result.user.getIdToken(true);
      
      // Store the token
      storeAuthToken(token);
      
      // Sign in with the backend
      const { data } = await signInMutation({
        variables: { token },
        refetchQueries: [CURRENT_USER],
        awaitRefetchQueries: true
      });
      
      return data?.signIn?.user;
    } catch (error: any) {
      ErrorLog(error);
      throw error;
    }
  };

  const value: AuthContextType = {
    authUser,
    currentUser: data?.me,
    loading: isAuthProgress || isInitializing || loading, // Include GraphQL query loading state
    isGuestSigningIn,
    signIn,
    signInWithGoogle,
    signInWithLinkedIn,
    signInWithEmailPassword,
    signInWithPhone,
    signInGuestContributor,
    signInWithInterviewLink,
    signInWithInviteLink,
    linkInterviewId,
    logout: () => auth.signOut(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}